import React, {
  Fragment, useEffect, useMemo, useRef, useState,
} from 'react';
import {
  Alert, Snackbar, ThemeProvider, Typography, useTheme,
} from '@mui/material';
import { Box } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation, Trans } from 'react-i18next';
import { unwrapResult } from '@reduxjs/toolkit';
import { ContentTheme } from './ContentTheme';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  ResetContentList,
  selectContentList,
  selectContentPageNumber,
  selectGetContentListState,
  selectHasMoreContent,
  UpdateHasMoreContent,
  UpdateIsContentPreview,
  UpdatePageNumber,
} from '../../../store/slices/contentSlice';
import { Content, SearchInteractionAction } from '../../../common/models/content/Content';
import { getContent } from '../../../store/thunks/contentThunk';
import {
  getHeaderImageUrl, getHeading, getHeroFormat, getImageUrl,
} from './ContentCommonFunctions';
import ContentHorizontalCardSkeleton from './ContentHorizontalCardSkeleton';
import ContentSearchView from './ContentSearchView';
import { useIsMobile } from '../../../common/hooks/useResponsive';
import { useCustomEventTracking } from '../../../common/useCustomEventTracking';
import ContentCard from './ContentCard';
import { useScrollRestoration } from '../../../common/hooks/useScrollRestoration';
import { useDisplayContentView } from '../../../common/hooks/useDisplayContentView';
import { useTrackCategoryContentVisibility } from './useTrackCategoryContentVisibility';
import { useTrackElementVisibilityInViewPort } from '../../../common/hooks/useTrackElementVisibilityInViewPort';
import { Logger } from '../../../config/logging/logger';

interface ContentSearchPageProps {
  contentViewBaseUrl: string;
  searchTerm?: string;
  topic?: string;
  businessGoal?: string;
  showSearchBar?: boolean;
  fullWidthContainer?: boolean
  setHasNoResults?: (value: boolean) => void
}

export default function ContentSearchPage({
  searchTerm,
  topic,
  businessGoal,
  fullWidthContainer = false,
  setHasNoResults,
  showSearchBar = true,
}: ContentSearchPageProps) {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [hideEndMessage, setHideEndMessage] = React.useState(false);
  const storeContentList = useAppSelector(selectContentList);
  const getContentListState = useAppSelector(selectGetContentListState);
  const storeHasMoreContent = useAppSelector(selectHasMoreContent);
  const page = useAppSelector(selectContentPageNumber); // the current value of page is from store
  const [contentList, setContentList] = useState(storeContentList);
  const [hasMoreContent, setHasMoreContent] = useState(storeHasMoreContent);
  const isMobile = useIsMobile();
  useTrackCategoryContentVisibility({
    categoryContentList: contentList,
  });

  const pageSize = isMobile ? 5 : 15;
  const navigate = useNavigate();
  const {
    trackSearchInteraction,
  } = useCustomEventTracking();
  const endMessageRef = useRef(null);
  const displayContentView = useDisplayContentView();
  const trackElementVisibility = useTrackElementVisibilityInViewPort();
  const logger = Logger.create('ContentSearchPage');
  const hasFetchedRef = useRef<string>('');
  const hasTrackedSearchRef = useRef<string>('');

  const isInitialLoading = useMemo(
    () => (getContentListState === 'pending' && page === 1),
    [
      getContentListState,
      page,
    ],
  );

  useEffect(() => {
    dispatch(UpdateIsContentPreview(false));
    setContentList(storeContentList);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeContentList]);

  useEffect(() => {
    setHasMoreContent(storeHasMoreContent);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeHasMoreContent]);

  const onNextPage = () => {
    if (page > 1) {
      trackSearchInteraction(
        'Load',
        'MoreContent',
        {
          action: SearchInteractionAction.ReachEnd,
        },
      );
    }

    dispatch(getContent({
      pageSize,
      page,
      searchTerm,
      topic,
      businessGoal,
      ...(setHasNoResults && { setHasNoResults }),
    }))
      .then(unwrapResult)
      .then((res) => {
        setHideEndMessage(false);
        dispatch(UpdateHasMoreContent(res.length === pageSize));
        if (hasMoreContent) {
          dispatch(UpdatePageNumber(page + 1));
        }
      }).catch((e) => {
        setHideEndMessage(true);
        logger.error(Logger.format(e));
      });
  };

  const { restoreScroll } = useScrollRestoration();

  useEffect(() => {
    if (restoreScroll()) {
      return;
    }

    // Prevent duplicate calls in StrictMode or re-renders
    const searchKey = `${searchTerm}-${businessGoal}-${topic}`;
    if (hasFetchedRef.current === searchKey) {
      return;
    }
    hasFetchedRef.current = searchKey;
    hasTrackedSearchRef.current = ''; // Reset tracking for new search

    dispatch(ResetContentList([])); // initial content list state
    dispatch(UpdatePageNumber(1)); // initial page count
    dispatch(getContent({
      pageSize,
      page: 1,
      searchTerm,
      topic,
      businessGoal,
      ...(setHasNoResults ? { setHasNoResults } : {}),
    }))
      .then(unwrapResult)
      .then((res) => {
        setOpenSnackbar(false);
        setHideEndMessage(false);
        dispatch(UpdatePageNumber(2));
        dispatch(UpdateHasMoreContent(res.length === pageSize));

        // Track events immediately after successful fetch, only once
        if (hasTrackedSearchRef.current !== searchKey) {
          hasTrackedSearchRef.current = searchKey;
          if (searchTerm) {
            trackSearchInteraction('Submit', 'Search', {
              action: SearchInteractionAction.Submit,
              searchTerm,
            });
          }

          trackSearchInteraction(
            'View',
            'ContentStart',
            {
              action: SearchInteractionAction.Start,
              feedVisit: new Date().toISOString(),
            },
          );
        }
      })
      .catch(() => {
        setOpenSnackbar(true);
        setHideEndMessage(true);
        dispatch(UpdateHasMoreContent(false));
      });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm, businessGoal, topic]);

  // Track when the user reaches the end of the feed
  useEffect(() => {
    const endMessageElement = document.getElementById('endMessage') as Element;

    const observers = trackElementVisibility([endMessageElement], () => {
      if (endMessageRef.current) {
        trackSearchInteraction(
          'View',
          'ContentEnd',
          {
            action: SearchInteractionAction.ReachEnd,
          },
        );
      }
    });
    // Clean up the observers when the component unmounts
    return () => {
      observers.forEach((observer) => observer.disconnect());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endMessageRef.current]);

  const handleClose = () => {
    setOpenSnackbar(false);
  };

  const handleSubmit = (value: string | undefined) => {
    const params = new URLSearchParams();
    const trimmedValue = value?.trim();
    if (!trimmedValue) return;

    params.set('query', trimmedValue);
    const route = `/search?${params.toString()}`;
    navigate(route);
  };

  const SearchBar = <Box
    sx={{
      padding: `${isMobile ? theme.spacing(3) : theme.spacing(2)} 0px 0px ${isMobile ? theme.spacing(2) : theme.spacing(3)}`,
      width: '100%',
    }}
  >
    <ContentSearchView
      onSubmit={handleSubmit}
      value={searchTerm}
    />
  </Box>;

  const ScrollEndMessage = <p id='endMessage' style={{
    textAlign: 'center', fontSize: theme.spacing(2.25), marginTop: theme.spacing(12.5), color: theme.palette.grey[500],
  }}
  ref={endMessageRef}>
    <b>
      <Trans
        i18nKey="content.feed.searchEndMessage"
        components={{
          // eslint-disable-next-line jsx-a11y/anchor-has-content
          topAnchorLink: <a href="#top-anchor"
            aria-label={t('content.feed.searchAgain') || ''}/>,
        }}
      />
    </b>

  </p>;

  const ScrollSkeleton = <ContentHorizontalCardSkeleton
    sx={fullWidthContainer ? { paddingLeft: 0 } : {}}
  />;

  const ContentViews = useMemo(() => (contentList || [])
    .map((contentItem: Content, indx: number) => (
      <Fragment key={`fragment-${contentItem.id}`}>
        <Box
          id={`optimus-content-${contentItem.id}`}
          sx={{
            marginBottom: theme.spacing(4),
            ...(contentItem.attributes?.type?.toUpperCase() === 'POLL' && {
              width: '60%',
              justifySelf: 'center',
              [theme.breakpoints.down('sm')]: {
                width: '100%',
              },
            }),
          }}
        >
          <ContentCard
            contentId={contentItem.id}
            contentHeroListener={() => displayContentView(contentItem)}
            contentText={contentItem.attributes?.title}
            contentShortDescription={contentItem.attributes?.short_description}
            categoryText={contentItem.attributes?.type}
            banner={
              {
                url: getImageUrl(
                  getHeroFormat(contentItem)?.url
                  ,
                ),
                width: getHeroFormat(contentItem)?.width,
                height: getHeroFormat(contentItem)?.height,
              }}
            authorTitle={getHeading(contentItem)}
            publishedDate={contentItem.attributes?.source_published_date}
            logo={getHeaderImageUrl(contentItem)}
            videoUrl={contentItem.attributes?.video_url}
            contentLikeStatus={contentItem.likeStatus}
            formId={contentItem.attributes?.form_id}
            metadata={contentItem.attributes?.metadata}
            podcast={contentItem.attributes?.podcast}
            locked={contentItem.attributes?.locked}
            lowestMembershipRequired={contentItem.attributes?.lowestMembershipRequired}
            isHorizontalCard={!isMobile}
          />
        </Box>
      </Fragment>
      // eslint-disable-next-line react-hooks/exhaustive-deps
    )), [contentList, fullWidthContainer, theme]);

  return (
    <ThemeProvider theme={ContentTheme}>
      <Snackbar open={openSnackbar} autoHideDuration={4000} onClose={handleClose}>
        <Alert severity="error" sx={{ width: '100%' }}>
          {t('error.generalMessage')}
        </Alert>
      </Snackbar>
      {showSearchBar && SearchBar}
      <Box
        sx={{
          display: 'flex',
          padding: !isMobile ? `${theme.spacing(2)} ${theme.spacing(4)}` : 0,
          flexDirection: 'column',
          alignItems: 'flex-start',
          alignSelf: 'stretch',
        }}
      >
        <>
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Typography
              variant='h12'
              marginBottom={theme.spacing(4)}
            >
              Results for '{searchTerm || topic || businessGoal}'
            </Typography>
            <InfiniteScroll
              scrollThreshold={0.8}
              dataLength={contentList.length} // This is important field to render the next data
              next={onNextPage}
              hasMore={hasMoreContent}
              style={{
                overflow: 'hidden',
                width: '100%',
              }}
              loader={ScrollSkeleton}
              endMessage={!hideEndMessage && ScrollEndMessage}
            >
              {isInitialLoading &&
                (ScrollSkeleton)
              }
              {!isInitialLoading &&
                (ContentViews)
              }
            </InfiniteScroll>
          </Box>
        </>
      </Box>
    </ThemeProvider >);
}
